import React, { useState, useEffect, useRef } from 'react';
import { FaSignature, FaUser, FaCalendar, FaSpinner, FaTimes, FaCheckCircle } from 'react-icons/fa';
import { API_ENDPOINTS } from '../../config/api';

const PeaknetSignModal = ({ contract, onClose, onSigned }) => {
  const [formData, setFormData] = useState({
    authorizedPersonName: '',
    authorizedPersonPosition: '',
    date: new Date().toISOString().split('T')[0],
    signature: null,
    initials: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [signatureCanvas, setSignatureCanvas] = useState(null);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [lastPos, setLastPos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      setSignatureCanvas(ctx);
    }
  }, []);

  // Helper to get coordinates from mouse or touch event
  const getCoordinates = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    if (e.touches && e.touches.length > 0) {
      // Touch event
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top
      };
    } else {
      // Mouse event
      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      };
    }
  };

  const startDrawing = (e) => {
    if (!signatureCanvas) return;
    e.preventDefault(); // Prevent scrolling on mobile
    setIsDrawing(true);
    const coords = getCoordinates(e);
    setLastPos(coords);
  };

  const draw = (e) => {
    if (!isDrawing || !signatureCanvas) return;
    e.preventDefault(); // Prevent scrolling on mobile
    const coords = getCoordinates(e);
    
    signatureCanvas.beginPath();
    signatureCanvas.moveTo(lastPos.x, lastPos.y);
    signatureCanvas.lineTo(coords.x, coords.y);
    signatureCanvas.stroke();
    
    setLastPos(coords);
  };

  const stopDrawing = (e) => {
    if (e) e.preventDefault();
    setIsDrawing(false);
  };

  const clearSignature = () => {
    if (signatureCanvas && canvasRef.current) {
      signatureCanvas.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      setFormData({ ...formData, signature: null });
    }
  };


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.authorizedPersonName.trim()) {
      setError('Please enter authorized full name');
      return;
    }

    if (!formData.authorizedPersonPosition.trim()) {
      setError('Please enter authorized person position');
      return;
    }

    if (!formData.date) {
      setError('Please enter date');
      return;
    }

    // Get signature from canvas
    if (!canvasRef.current) {
      setError('Please provide your signature');
      return;
    }

    const signatureBase64 = canvasRef.current.toDataURL('image/png').split(',')[1];
    if (!signatureBase64 || signatureBase64.length < 100) {
      setError('Please provide your signature');
      return;
    }

    // Initials is now a text field, convert to image for PDF
    if (!formData.initials || !formData.initials.trim()) {
      setError('Please provide your initials');
      return;
    }
    
    // Create a canvas to render initials as text image
    const initialsCanvas = document.createElement('canvas');
    initialsCanvas.width = 300;
    initialsCanvas.height = 100;
    const ctx = initialsCanvas.getContext('2d');
    ctx.fillStyle = '#000000';
    ctx.font = 'bold 48px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(formData.initials.trim().toUpperCase(), initialsCanvas.width / 2, initialsCanvas.height / 2);
    const initialsBase64 = initialsCanvas.toDataURL('image/png').split(',')[1];

    try {
      setSubmitting(true);

      const response = await fetch(API_ENDPOINTS.CONTRACT_MANAGEMENT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken') || ''}`
        },
        body: JSON.stringify({
          action: 'submitPeaknetSignature',
          contractId: contract.contractId,
          authorizedPersonName: formData.authorizedPersonName.trim(),
          authorizedPersonPosition: formData.authorizedPersonPosition.trim(),
          date: formData.date,
          signature: signatureBase64,
          initials: initialsBase64
        })
      });

      const data = await response.json();

      if (data.success) {
        if (onSigned) {
          onSigned();
        }
        onClose();
      } else {
        setError(data.error || 'Failed to submit signature. Please try again.');
      }
    } catch (err) {
      console.error('Error submitting signature:', err);
      setError('Failed to submit signature. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#282828] rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-[#3c3836] flex justify-between items-center">
          <h2 className="text-2xl font-bold text-[#ebdbb2]">Sign Contract as Peaknet</h2>
          <button
            onClick={onClose}
            className="text-[#a89984] hover:text-[#ebdbb2]"
            disabled={submitting}
          >
            <FaTimes size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Contract Info */}
          <div className="bg-[#3c3836] rounded-lg p-4">
            <h3 className="text-lg font-semibold text-[#ebdbb2] mb-2">Contract Information</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-[#a89984]">Contract ID:</span>
                <span className="text-[#ebdbb2] ml-2">{contract.contractNumber || contract.contractId}</span>
              </div>
              <div>
                <span className="text-[#a89984]">Customer:</span>
                <span className="text-[#ebdbb2] ml-2">{contract.customerName}</span>
              </div>
              {contract.signedCompanyName && (
                <div>
                  <span className="text-[#a89984]">Customer Company:</span>
                  <span className="text-[#ebdbb2] ml-2">{contract.signedCompanyName}</span>
                </div>
              )}
              {contract.customerSignedAt && (
                <div>
                  <span className="text-[#a89984]">Customer Signed:</span>
                  <span className="text-[#ebdbb2] ml-2">
                    {new Date(contract.customerSignedAt).toLocaleDateString('en-AU')}
                  </span>
                </div>
              )}
            </div>
          </div>

          {error && (
            <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4">
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Authorized Full Name */}
            <div>
              <label className="block text-sm font-medium text-[#a89984] mb-2">
                <FaUser className="inline mr-2" />
                Peaknet Authorized Full Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                name="authorizedPersonName"
                value={formData.authorizedPersonName}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-[#282828] border border-[#504945] rounded-lg text-[#ebdbb2] focus:outline-none focus:border-[#FFD700] placeholder-[#7c6f64]"
                placeholder="Enter authorized person full name"
                required
                disabled={submitting}
              />
            </div>

            {/* Authorized Person Position */}
            <div>
              <label className="block text-sm font-medium text-[#a89984] mb-2">
                <FaUser className="inline mr-2" />
                Position <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                name="authorizedPersonPosition"
                value={formData.authorizedPersonPosition}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-[#282828] border border-[#504945] rounded-lg text-[#ebdbb2] focus:outline-none focus:border-[#FFD700] placeholder-[#7c6f64]"
                placeholder="Enter position (e.g., Director, CEO, Manager)"
                required
                disabled={submitting}
              />
            </div>

            {/* Date */}
            <div>
              <label className="block text-sm font-medium text-[#a89984] mb-2">
                <FaCalendar className="inline mr-2" />
                Date <span className="text-red-400">*</span>
              </label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-[#282828] border border-[#504945] rounded-lg text-[#ebdbb2] focus:outline-none focus:border-[#FFD700]"
                required
                disabled={submitting}
              />
            </div>

            {/* Signature */}
            <div>
              <label className="block text-sm font-medium text-[#a89984] mb-2">
                <FaSignature className="inline mr-2" />
                Peaknet Signature <span className="text-red-400">*</span>
              </label>
              <div className="border-2 border-[#504945] rounded-lg bg-white p-2" style={{ touchAction: 'none', userSelect: 'none' }}>
                <canvas
                  ref={canvasRef}
                  width={600}
                  height={200}
                  className="w-full border border-gray-300 rounded cursor-crosshair"
                  style={{ touchAction: 'none', display: 'block' }}
                  onMouseDown={(e) => {
                    if (!signatureCanvas) return;
                    e.preventDefault();
                    e.stopPropagation();
                    const rect = canvasRef.current.getBoundingClientRect();
                    const scaleX = canvasRef.current.width / rect.width;
                    const scaleY = canvasRef.current.height / rect.height;
                    const x = (e.clientX - rect.left) * scaleX;
                    const y = (e.clientY - rect.top) * scaleY;
                    setIsDrawing(true);
                    signatureCanvas.beginPath();
                    signatureCanvas.moveTo(x, y);
                    setLastPos({ x, y });
                  }}
                  onMouseMove={(e) => {
                    if (!isDrawing || !signatureCanvas) return;
                    e.preventDefault();
                    e.stopPropagation();
                    const rect = canvasRef.current.getBoundingClientRect();
                    const scaleX = canvasRef.current.width / rect.width;
                    const scaleY = canvasRef.current.height / rect.height;
                    const x = (e.clientX - rect.left) * scaleX;
                    const y = (e.clientY - rect.top) * scaleY;
                    signatureCanvas.lineTo(x, y);
                    signatureCanvas.stroke();
                    setLastPos({ x, y });
                  }}
                  onMouseUp={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDrawing(false);
                  }}
                  onMouseLeave={(e) => {
                    e.preventDefault();
                    setIsDrawing(false);
                  }}
                  onTouchStart={(e) => {
                    if (!signatureCanvas) return;
                    e.preventDefault();
                    e.stopPropagation();
                    const rect = canvasRef.current.getBoundingClientRect();
                    const scaleX = canvasRef.current.width / rect.width;
                    const scaleY = canvasRef.current.height / rect.height;
                    const touch = e.touches[0];
                    const x = (touch.clientX - rect.left) * scaleX;
                    const y = (touch.clientY - rect.top) * scaleY;
                    setIsDrawing(true);
                    signatureCanvas.beginPath();
                    signatureCanvas.moveTo(x, y);
                    setLastPos({ x, y });
                  }}
                  onTouchMove={(e) => {
                    if (!isDrawing || !signatureCanvas) return;
                    e.preventDefault();
                    e.stopPropagation();
                    const rect = canvasRef.current.getBoundingClientRect();
                    const scaleX = canvasRef.current.width / rect.width;
                    const scaleY = canvasRef.current.height / rect.height;
                    const touch = e.touches[0];
                    const x = (touch.clientX - rect.left) * scaleX;
                    const y = (touch.clientY - rect.top) * scaleY;
                    signatureCanvas.lineTo(x, y);
                    signatureCanvas.stroke();
                    setLastPos({ x, y });
                  }}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDrawing(false);
                  }}
                  onTouchCancel={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDrawing(false);
                  }}
                />
              </div>
              <button
                type="button"
                onClick={clearSignature}
                className="mt-2 text-sm text-[#a89984] hover:text-[#ebdbb2] transition-colors"
                disabled={submitting}
              >
                Clear Signature
              </button>
            </div>

            {/* Initials */}
            <div>
              <label className="block text-sm font-medium text-[#a89984] mb-2">
                <FaSignature className="inline mr-2" />
                Peaknet Initials <span className="text-red-400">*</span>
                <span className="text-xs text-[#7c6f64] ml-2">(Enter your initials)</span>
              </label>
              <input
                type="text"
                name="initials"
                value={formData.initials || ''}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-[#282828] border border-[#504945] rounded-lg text-[#ebdbb2] focus:outline-none focus:border-[#FFD700] placeholder-[#7c6f64] text-2xl font-semibold uppercase"
                placeholder="Enter initials (e.g., JD)"
                required
                disabled={submitting}
                maxLength={5}
                style={{ letterSpacing: '0.2em' }}
              />
            </div>

            {/* Submit Button */}
            <div className="flex justify-end space-x-4 pt-4 border-t border-[#3c3836]">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 bg-[#3c3836] text-[#ebdbb2] rounded-lg hover:bg-[#504945] transition-colors"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-3 bg-[#FFD700] text-[#1d2021] rounded-lg hover:bg-[#FFB700] transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <FaCheckCircle />
                    <span>Finalize Contract</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PeaknetSignModal;

